import React, { useMemo, useState, useCallback } from 'react';
import { colors } from '@naf-simba/theme';
import { TravelSuggestion } from '../../../../types/TravelSuggestionType';
import * as S from './styles';
import { RouteBox } from './RouteBox';
import { getGoogleMapsLink, getPOIIcon } from './utils';
import { MapWrapper, Marker, Polyline } from '../../loadable-elements/LoadableGoogleMapsAPI';
import { decodePolyline } from './polylineUtils';
import { useMapsApiRouteData } from '../../hooks/useMapsApiRouteData';
import { RichGeopoint } from '../../../../types/routeBlockType';

const MAP_OPTIONS = {
  mapId: '241506239fc98522',
  streetViewControl: false,
  mapTypeControl: false,
  disableDefaultUI: true,
};

const DEFAULT_ZOOM = 5;

type TravelMapV2Props = {
  routePoints: TravelSuggestion['routePoints'];
  travelMode: TravelSuggestion['travelMode'];
};

const TravelMapV2 = ({ routePoints, travelMode }: TravelMapV2Props) => {
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  // Convert routePoints to RichGeopoint[] format for the hook
  const richGeopoints = useMemo<RichGeopoint[]>(
    () =>
      routePoints?.map((point) => ({
        latlng: { lat: point.latitude, lng: point.longitude },
        title: point.title || '',
        includeOnChart: false, // TravelMapV2 doesn't use chart POIs
      })) || [],
    [routePoints],
  );

  const { routeData, error, isLoading } = useMapsApiRouteData({
    routepoints: richGeopoints,
    travelMode,
  });

  const markers = useMemo(
    () =>
      routePoints?.map((point) => ({
        ...point,
        latlng: { lat: point.latitude, lng: point.longitude }, // Use plain object for SSR safety
      })),
    [routePoints],
  );

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      if (map && markers) {
        if (mapInstance) {
          return; // Only run once
        }
        setMapInstance(map);
        const bounds = new google.maps.LatLngBounds();
        markers.forEach((marker) => {
          bounds.extend(marker.latlng);
        });
        map.fitBounds(bounds);
        const initialZoom = map.getZoom();
        if (initialZoom) {
          map.setZoom(initialZoom - 1);
        }
      }
    },
    [markers, mapInstance],
  );

  // Decode polyline for rendering
  const decodedPath = useMemo(() => {
    if (!routeData) return null;
    return decodePolyline(routeData.polyline);
  }, [routeData]);

  return (
    <S.MapContainer>
      <MapWrapper onLoad={onLoad} zoom={DEFAULT_ZOOM} mapOptions={MAP_OPTIONS}>
        {mapInstance && (
          <>
            {/* Render polyline */}
            {decodedPath && (
              <Polyline
                path={decodedPath}
                options={{
                  strokeColor: colors.secondary[900],
                  strokeOpacity: 1.0,
                  strokeWeight: 4,
                }}
              />
            )}

            {/* Render markers */}
            {markers.map((marker, index) => (
              <Marker
                key={`${marker.longitude}-${marker.latitude}`}
                position={{
                  lat: marker.latitude,
                  lng: marker.longitude,
                }}
                icon={{
                  url: getPOIIcon(index, markers.length - 1),
                  scale: 8,
                }}
                options={{
                  zIndex: index === 0 || index === markers.length - 1 ? 10 : 1,
                }}
              />
            ))}

            {/* Show loading or error state */}
            {isLoading && <div>Laster rute...</div>}
            {error && <div>Kunne ikke laste rute</div>}
          </>
        )}
      </MapWrapper>
      {mapInstance && (
        <RouteBox
          routePoints={routePoints}
          googleMapsLink={getGoogleMapsLink({ routePoints, travelMode, isMapLoaded: !!mapInstance })}
        />
      )}
    </S.MapContainer>
  );
};

export default TravelMapV2;
